import React, { Suspense } from 'react';
import { Box, Divider, MainContentBox, Typography } from 'enova-frontend-components';
import { useTranslation } from 'react-i18next';
import { Navigate, Outlet, useParams } from 'react-router-dom';
import { Helmet } from 'react-helmet';

import RouterLink from '../../components/routerLink';
import {
  AllRegistrationScreenParams,
  AppRoute,
  BuildingScreenTab,
  getBuildingScreenPath,
  hasPermission
} from '../../utils/navigation';
import BuildingHeaderInfo from '../../components/buildingHeaderInfo';
import BuildingDetailsProvider from '../../hooks/useBuildingDetails/provider';
import { SoknadSteg } from '../../components/registering/utils/registerEnums';
import { useGetBuildingAddress } from '../../hooks/useGetBuildingAddress';
import { useBuildingData } from '../../hooks/useBuildingDetails/buildingDetailsHooks';
import { LoadingIndicator } from '../../components/loadingIndicator';
import { useUser } from '../../hooks/useUser';

const SoknadLayout = () => {
  const { t } = useTranslation();
  const { getBuildingAddress } = useGetBuildingAddress();
  const { user } = useUser();

  const { soknadSteg = SoknadSteg.Steg1, buildingId } = useParams<AllRegistrationScreenParams>();

  const { building, buildingLoading } = useBuildingData(buildingId);

  if (buildingLoading) {
    return <LoadingIndicator />;
  }

  if (!hasPermission(AppRoute.SOKNAD, user) || !buildingId || !building) {
    return <Navigate to="../" replace />;
  }

  const buildingPath = getBuildingScreenPath({
    buildingId,
    tab: BuildingScreenTab.DOCUMENTS
  });

  const helmetTitle = [
    t('registerScreen.pageTitle', {
      context: soknadSteg
    }),
    getBuildingAddress(building.adresse)
  ].join(' | ');

  return (
    <BuildingDetailsProvider>
      <Helmet title={helmetTitle} />

      <MainContentBox>
        <RouterLink className="mb-3 as-start" to={buildingPath}>
          {t('navigation.registerScreen.back')}
        </RouterLink>

        <Box display="flex" alignItems="flex-start" flexDirection="column">
          <Typography variant="h1" mb={0}>
            {t('registerScreen.pageTitle', { context: soknadSteg })}
          </Typography>

          <BuildingHeaderInfo energiobjektId={buildingId} AddressProps={{ variant: 'h2', component: 'p' }} />
        </Box>
      </MainContentBox>

      <Divider />

      <Suspense fallback={<LoadingIndicator text={t('register.soknad.loading')} />}>
        <MainContentBox>
          <Outlet />
        </MainContentBox>
      </Suspense>
    </BuildingDetailsProvider>
  );
};

export default SoknadLayout;
